import type { ExtractedMesh } from "@/core/mesh/surfaceNets";

/**
 * Binary STL, in millimetres.
 *
 * The app models with +y as the mounting axis (Three.js is y-up, and the hold
 * sits on the bench that way). Slicers expect z-up with the part on the build
 * plate, so the writer rotates model space into slicer space:
 *
 *     stl.x =  model.x
 *     stl.y = -model.z
 *     stl.z =  model.y
 *
 * That is a proper rotation (determinant +1), so triangle winding — and
 * therefore outward-facing normals — survives unchanged. The flat rear face
 * lands on z = 0, ready to slice with no reorientation.
 */

export function toSlicerSpace(
  x: number,
  y: number,
  z: number,
): [number, number, number] {
  return [x, -z, y];
}

export interface StlStats {
  triangleCount: number;
  byteLength: number;
  /** Bounding box in slicer space, mm. */
  min: [number, number, number];
  max: [number, number, number];
}

export function meshToBinaryStl(
  mesh: ExtractedMesh,
  headerText = "ETHOS Hold Shaper V1.1",
): { buffer: ArrayBuffer; stats: StlStats } {
  const triangleCount = mesh.indices.length / 3;
  const byteLength = 84 + triangleCount * 50;
  const buffer = new ArrayBuffer(byteLength);
  const view = new DataView(buffer);
  const bytes = new Uint8Array(buffer);

  const header = headerText.slice(0, 79);
  for (let i = 0; i < header.length; i++) bytes[i] = header.charCodeAt(i) & 0x7f;
  view.setUint32(80, triangleCount, true);

  const min: [number, number, number] = [Infinity, Infinity, Infinity];
  const max: [number, number, number] = [-Infinity, -Infinity, -Infinity];

  let offset = 84;
  const p = mesh.positions;
  for (let t = 0; t < mesh.indices.length; t += 3) {
    const ia = (mesh.indices[t] as number) * 3;
    const ib = (mesh.indices[t + 1] as number) * 3;
    const ic = (mesh.indices[t + 2] as number) * 3;

    const a = toSlicerSpace(p[ia] as number, p[ia + 1] as number, p[ia + 2] as number);
    const b = toSlicerSpace(p[ib] as number, p[ib + 1] as number, p[ib + 2] as number);
    const c = toSlicerSpace(p[ic] as number, p[ic + 1] as number, p[ic + 2] as number);

    const ux = b[0] - a[0];
    const uy = b[1] - a[1];
    const uz = b[2] - a[2];
    const vx = c[0] - a[0];
    const vy = c[1] - a[1];
    const vz = c[2] - a[2];
    let nx = uy * vz - uz * vy;
    let ny = uz * vx - ux * vz;
    let nz = ux * vy - uy * vx;
    const len = Math.hypot(nx, ny, nz);
    if (len > 0) {
      nx /= len;
      ny /= len;
      nz /= len;
    } else {
      nx = 0;
      ny = 0;
      nz = 0;
    }

    view.setFloat32(offset, nx, true);
    view.setFloat32(offset + 4, ny, true);
    view.setFloat32(offset + 8, nz, true);
    offset += 12;
    for (const v of [a, b, c]) {
      view.setFloat32(offset, v[0], true);
      view.setFloat32(offset + 4, v[1], true);
      view.setFloat32(offset + 8, v[2], true);
      offset += 12;
      for (let axis = 0; axis < 3; axis++) {
        const value = v[axis] as number;
        if (value < (min[axis] as number)) min[axis] = value;
        if (value > (max[axis] as number)) max[axis] = value;
      }
    }
    view.setUint16(offset, 0, true);
    offset += 2;
  }

  if (!Number.isFinite(min[0])) {
    min[0] = min[1] = min[2] = 0;
    max[0] = max[1] = max[2] = 0;
  }
  return { buffer, stats: { triangleCount, byteLength, min, max } };
}

export interface ParsedStl {
  triangleCount: number;
  /** Flat xyz triples, three vertices per triangle. */
  vertices: Float32Array;
  normals: Float32Array;
}

/** Minimal reader, used by the geometry tests to check what we actually wrote. */
export function parseBinaryStl(buffer: ArrayBuffer): ParsedStl {
  const view = new DataView(buffer);
  const triangleCount = view.getUint32(80, true);
  if (buffer.byteLength < 84 + triangleCount * 50) {
    throw new Error("Truncated binary STL");
  }
  const vertices = new Float32Array(triangleCount * 9);
  const normals = new Float32Array(triangleCount * 3);
  let offset = 84;
  for (let t = 0; t < triangleCount; t++) {
    normals[t * 3] = view.getFloat32(offset, true);
    normals[t * 3 + 1] = view.getFloat32(offset + 4, true);
    normals[t * 3 + 2] = view.getFloat32(offset + 8, true);
    offset += 12;
    for (let v = 0; v < 9; v++) {
      vertices[t * 9 + v] = view.getFloat32(offset, true);
      offset += 4;
    }
    offset += 2;
  }
  return { triangleCount, vertices, normals };
}
